import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Row, Col, Badge } from 'react-bootstrap';
import Flex from '../../../common/Flex';
import ProfileBanner from '../ProfileBanner';
import ProfileImageUpload from './ProfileImageUpload';
import coverSrc from '../../../../assets/img/generic/4.jpg';
import { useProfileImage } from '../../../../hooks/useProfileImage';

const Banner = ({ user, onImageUpdate }) => {
  const { imageKey, imageSrc } = useProfileImage(user);
  
  if (!user) return null;

  const getRoleBadgeVariant = (roleName) => {
    switch (roleName?.toLowerCase()) {
      case 'super_admin':
      case 'super admin':
        return 'danger';
      case 'admin':
      case 'administrador':
        return 'warning';
      case 'manager':
      case 'gerente':
        return 'info';
      case 'user':
      case 'usuario':
        return 'primary';
      case 'viewer':
      case 'visualizador':
        return 'secondary';
      default:
        return 'light';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Calcular posición basada en roles
  const getUserPosition = (roles) => {
    if (!roles || roles.length === 0) return 'Usuario';
    
    const roleHierarchy = {
      'SUPER_ADMIN': 'Super Administrador',
      'ADMIN': 'Administrador',
      'MANAGER': 'Gerente',
      'USER': 'Usuario',
      'VIEWER': 'Visualizador'
    };

    for (const roleKey of Object.keys(roleHierarchy)) {
      if (roles.some(role => role.name === roleKey)) {
        return roleHierarchy[roleKey];
      }
    }

    return roles[0].name;
  };

  return (
    <ProfileBanner>
      <ProfileBanner.Header 
        key={`banner-${imageKey}-${user.id}`} // Forzar re-render cuando cambie la imagen
        avatar={imageSrc} 
        avatarKey={`avatar-${imageKey}-${user.id}`} // Key específico para el Avatar
        coverSrc={coverSrc} 
        avatarName={user.fullName}
      />
      <ProfileBanner.Body>
        <Row>
          <Col lg={8}>
            <h4 className="mb-1 fw-bold">{user.fullName}</h4>
            <h5 className="fs-9 fw-normal text-muted">
              <FontAwesomeIcon icon="briefcase" className="me-2" />
              {getUserPosition(user.roles)}
            </h5>
            <p className="text-500 mb-3">
              <FontAwesomeIcon icon="building" className="me-2" />
              {user.company?.name || 'No definida'}
              {user.businessAreas && user.businessAreas.length > 0 && (
                <span className="ms-2">
                  • {user.businessAreas[0].name}
                </span>
              )}
            </p>
            
            {/* Información adicional */}
            <div className="mb-3">
              <Flex className="mb-1">
                <FontAwesomeIcon icon="envelope" className="me-2 text-muted" />
                <span className="text-700">{user.email}</span>
              </Flex>
              <Flex className="mb-1">
                <FontAwesomeIcon icon="user" className="me-2 text-muted" />
                <span className="text-700">@{user.username}</span>
              </Flex>
              <Flex className="mb-1">
                <FontAwesomeIcon icon="calendar" className="me-2 text-muted" />
                <span className="text-700">
                  Miembro desde {formatDate(user.createdAt)}
                </span>
              </Flex>
            </div>

            {/* Estado y botones */}
            <div className="mb-2">
              {user.isActive ? (
                <Badge bg="success" className="me-2">
                  <FontAwesomeIcon icon="check-circle" className="me-1" />
                  Activo
                </Badge>
              ) : (
                <Badge bg="danger" className="me-2">
                  <FontAwesomeIcon icon="times-circle" className="me-1" />
                  Inactivo
                </Badge>
              )}
              
              {user.isSuperAdmin && (
                <Badge bg="danger" className="me-2">
                  <FontAwesomeIcon icon="crown" className="me-1" />
                  Super Admin
                </Badge>
              )}
            </div>

            {/* Botón para cambiar foto - VISIBLE Y CLARO */}
            <div className="mb-3">
              <ProfileImageUpload 
                user={user}
                onImageUpdate={onImageUpdate}
                showAsButton={true}
              />
            </div>

            <div className="border-dashed border-bottom my-4 d-lg-none" />
          </Col>
          
          <Col className="ps-2 ps-lg-3">
            {/* Roles */}
            <div className="mb-3">
              <h6 className="text-muted mb-2">
                <FontAwesomeIcon icon="user-shield" className="me-1" />
                Roles
              </h6>
              {user.roles?.map((role, index) => (
                <Badge 
                  key={index}
                  bg={getRoleBadgeVariant(role.name)} 
                  className="me-1 mb-1 fs-10"
                >
                  {role.name}
                </Badge>
              ))}
            </div>

            {/* Información de la empresa */}
            <div>
              <h6 className="text-muted mb-2">
                <FontAwesomeIcon icon="building" className="me-1" />
                Empresa
              </h6>
              <Flex alignItems="center" className="mb-2">
                <FontAwesomeIcon
                  icon="building"
                  className="me-2 text-700"
                  style={{ width: '16px' }}
                />
                <div className="flex-1">
                  <h6 className="mb-0 fs-10">{user.company?.name}</h6>
                  <small className="text-muted">
                    Plan: {user.company?.plan || 'básico'}
                  </small>
                </div>
              </Flex>
            </div>
          </Col>
        </Row>
      </ProfileBanner.Body>
    </ProfileBanner>
  );
};

export default Banner; 